import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { 
  GraduationCap, ArrowLeft, CheckCircle, XCircle, Brain, 
  Lightbulb, LogOut, BarChart3, Target, Trash2
} from "lucide-react";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubmissionDetail() {
  const { user, logout } = useAuth();
  const { id } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const fetchSubmission = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/submissions/${id}`);
      setSubmission(response.data);
    } catch (error) {
      toast.error("Failed to load submission");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/submissions/${id}`);
      toast.success("Submission deleted successfully");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete submission");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen graph-paper-bg flex items-center justify-center" role="main">
        {/* WCAG: Screen reader announcement */}
        <div role="status" aria-live="polite" className="sr-only">Loading submission results</div>
        <div 
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"
          role="progressbar"
          aria-label="Loading results"
        ></div>
      </div>
    );
  }

  if (!submission) {
    return null;
  }

  // WCAG: Text labels along with colors for status
  const getScoreStatus = (percentage) => {
    if (percentage >= 70) return { color: 'text-green-700', bg: 'bg-green-100', label: 'Great Job!' };
    if (percentage >= 50) return { color: 'text-amber-700', bg: 'bg-amber-100', label: 'Good Effort!' };
    return { color: 'text-red-700', bg: 'bg-red-100', label: 'Keep Practicing!' };
  };

  const scoreStatus = getScoreStatus(submission.percentage);
  const weaknesses = Object.entries(submission.weakness_analysis || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="min-h-screen graph-paper-bg">
      {/* WCAG: Skip to main content target */}
      <a href="#main-content" className="skip-link">Skip to main content</a>
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40" role="banner">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
              aria-label="Go back to dashboard"
              tabIndex={0}
            >
              <ArrowLeft className="w-5 h-5" aria-hidden="true" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center" aria-hidden="true">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Submission Results
                </h1>
                <p className="text-sm text-gray-600">{submission.answer_key_name || "Test Results"}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              {/* COPPA: Display anonymous name instead of PII */}
              <p className="font-medium text-gray-900">{user?.display_name || "Student"}</p>
              <p className="text-sm text-gray-600">{user?.role || "Learner"}</p>
            </div>
            
            {/* Delete Submission Button */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  aria-label="Delete submission"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Submission?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete this submission and all associated files. 
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
              aria-label="Sign out"
              tabIndex={0}
            >
              <LogOut className="w-5 h-5" aria-hidden="true" />
              <span className="sr-only">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-6 py-8" role="main" tabIndex={-1}>
        {/* Score Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="overflow-hidden" data-testid="score-card">
            <div className={`${scoreStatus.bg} p-8`}>
              <div className="flex items-center justify-between">
                <div>
                  {/* WCAG: Text label for status, not just color */}
                  <p className="text-sm font-medium text-gray-700 mb-1">Your Score</p>
                  <p className={`text-lg font-semibold ${scoreStatus.color} mb-2`} role="status">
                    {scoreStatus.label}
                  </p>
                  <div className="flex items-baseline gap-3">
                    <span 
                      className={`text-6xl font-bold ${scoreStatus.color} font-mono`}
                      aria-label={`Score: ${submission.percentage} percent`}
                    >
                      {submission.percentage}%
                    </span>
                    <span className="text-2xl text-gray-600 font-mono" aria-label={`${submission.score} out of ${submission.total} correct`}>
                      {submission.score}/{submission.total}
                    </span>
                  </div>
                </div>
                <div 
                  className={`w-24 h-24 ${scoreStatus.bg} rounded-full flex items-center justify-center border-4 ${
                    submission.percentage >= 70 ? 'border-green-400' :
                    submission.percentage >= 50 ? 'border-amber-400' : 'border-red-400'
                  }`}
                  aria-hidden="true"
                >
                  <Target className={`w-12 h-12 ${scoreStatus.color}`} />
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-8">
                {/* WCAG: Icon + text label, not just color */}
                <div className="flex items-center gap-4" role="group" aria-label="Correct answers">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-700" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-green-700 font-mono">
                      {submission.correct_answers?.length || 0}
                    </p>
                    <p className="text-sm text-gray-700 flex items-center gap-1">
                      <span aria-hidden="true">✓</span> Correct Answers
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4" role="group" aria-label="Incorrect answers">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-700" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-red-700 font-mono">
                      {submission.incorrect_answers?.length || 0}
                    </p>
                    <p className="text-sm text-gray-700 flex items-center gap-1">
                      <span aria-hidden="true">✗</span> Needs Practice
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Weakness Analysis */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card data-testid="weakness-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Brain className="w-5 h-5 text-amber-600" aria-hidden="true" />
                  Areas to Improve
                </CardTitle>
              </CardHeader>
              <CardContent>
                {weaknesses.length === 0 ? (
                  <div className="text-center py-8" role="status">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" aria-hidden="true" />
                    <p className="text-gray-700 font-medium">
                      <span aria-hidden="true">🎉</span> Excellent! No areas needing extra practice.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4" role="list" aria-label="Topics to practice">
                    {weaknesses.map(([concept, count], index) => {
                      const maxCount = weaknesses[0][1];
                      const percentage = (count / maxCount) * 100;
                      return (
                        <motion.div 
                          key={concept}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 * index }}
                          className="space-y-2"
                          role="listitem"
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-800">{concept}</span>
                            {/* WCAG: Clear text label, not just badge */}
                            <Badge variant="outline" className="font-mono text-gray-700">
                              {count} {count === 1 ? 'question to review' : 'questions to review'}
                            </Badge>
                          </div>
                          <Progress 
                            value={percentage} 
                            className="h-3" 
                            aria-label={`${concept}: ${count} mistakes`}
                          />
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* AI Recommendations - Pedagogy: Hints not answers */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card data-testid="recommendations-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Lightbulb className="w-5 h-5 text-amber-500" aria-hidden="true" />
                  Study Hints & Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!submission.recommendations || submission.recommendations.length === 0 ? (
                  <div className="text-center py-8" role="status">
                    <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-4" aria-hidden="true" />
                    <p className="text-gray-600">Keep practicing to get personalized hints!</p>
                  </div>
                ) : (
                  <div className="space-y-4" role="list" aria-label="Study recommendations">
                    {submission.recommendations.map((rec, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className="flex gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200"
                        role="listitem"
                      >
                        <div 
                          className="w-8 h-8 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0"
                          aria-hidden="true"
                        >
                          <span className="font-bold text-amber-800 font-mono">{index + 1}</span>
                        </div>
                        <p className="text-gray-800 leading-relaxed">{rec}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Answer Breakdown - WCAG: Not color-only feedback */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8"
        >
          <Card data-testid="answer-breakdown-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <BarChart3 className="w-5 h-5 text-primary" aria-hidden="true" />
                Answer Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Table view for clarity */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="grid" aria-label="Answer comparison table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="p-2 text-left font-medium text-gray-600">Q#</th>
                      <th className="p-2 text-center font-medium text-gray-600">Your Answer</th>
                      <th className="p-2 text-center font-medium text-gray-600">Correct Answer</th>
                      <th className="p-2 text-center font-medium text-gray-600">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: submission.total }, (_, i) => {
                      const questionNum = i + 1;
                      const isCorrect = submission.correct_answers?.includes(questionNum);
                      const studentAnswer = submission.extracted_answers?.[String(questionNum)] || "?";
                      // Note: correct_answer_key is passed from backend in the submission
                      const correctAnswer = submission.correct_answer_key?.[String(questionNum)] || "-";
                      
                      return (
                        <tr 
                          key={questionNum}
                          className={`border-b ${isCorrect ? 'bg-green-50' : 'bg-red-50'}`}
                        >
                          <td className="p-2 font-medium">Q{questionNum}</td>
                          <td className="p-2 text-center">
                            <span className={`font-mono font-bold text-lg ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                              {studentAnswer}
                            </span>
                          </td>
                          <td className="p-2 text-center">
                            <span className="font-mono font-bold text-lg text-blue-700">
                              {correctAnswer}
                            </span>
                          </td>
                          <td className="p-2 text-center">
                            {isCorrect ? (
                              <span className="inline-flex items-center gap-1 text-green-700">
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-xs">Correct</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-700">
                                <XCircle className="w-4 h-4" />
                                <span className="text-xs">Wrong</span>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Summary */}
              <div className="flex items-center gap-6 mt-6 pt-6 border-t" role="legend">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-700">Correct: {submission.correct_answers?.length || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-gray-700">Wrong: {submission.incorrect_answers?.length || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Back to Dashboard */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate("/dashboard")}
            variant="outline"
            className="px-8"
            data-testid="back-to-dashboard-btn"
            aria-label="Return to dashboard"
            tabIndex={0}
          >
            <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
            Back to Dashboard
          </Button>
        </div>
      </main>
    </div>
  );
}
